import { useState } from 'react';
import { MapPin, Mail, Clock, Send, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Contato = () => {
  const { toast } = useToast();
  const { settings, loading } = useSiteSettings();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const trackContact = async (type) => {
    try {
      await axios.post(`${BACKEND_URL}/api/track/contact?type=${type}`);
    } catch (e) {
      console.log('Track error');
    }
  };

  const handleEmailClick = () => {
    trackContact('email');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    trackContact('email');
    
    const emailBody = encodeURIComponent(
      `Nome: ${formData.name}\n` +
      `Email: ${formData.email}\n` +
      `Telefone: ${formData.phone}\n` +
      `Assunto: ${formData.subject}\n\n` +
      `Mensagem:\n${formData.message}`
    );
    
    window.location.href = `mailto:${settings.site_email}?subject=${encodeURIComponent(formData.subject)}&body=${emailBody}`;
    
    toast({
      title: "Redirecionando para seu cliente de e-mail",
      description: "Complete o envio através do seu aplicativo de e-mail.",
    });
  };

  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 10) {
      return numbers.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d)/, '$1-$2');
    } else {
      return numbers.slice(0, 11).replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2');
    }
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    setFormData({ ...formData, phone: formatted });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="contato-page">
      {/* Hero */}
      <section className="bg-[#0A0A0A] text-white py-20 md:py-28">
        <div className="container mx-auto px-6 md:px-12">
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#EA1266] mb-4 block">Fale Conosco</span>
          <h1 className="text-4xl sm:text-5xl font-medium tracking-tight" data-testid="contato-title">
            Entre em Contato
          </h1>
          <p className="text-base text-gray-400 mt-4 font-medium max-w-lg">
            Estamos prontos para atender você com toda dedicação
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Info */}
            <div>
              <h2 className="text-2xl sm:text-3xl font-medium text-[#0A0A0A] mb-8 tracking-tight">Informações de Contato</h2>

              <div className="space-y-4">
                <Card className="bg-white rounded-2xl border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(234,18,102,0.08)] transition-all">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="bg-[#EA1266]/10 p-3 rounded-xl flex-shrink-0">
                      <MapPin className="text-[#EA1266]" size={22} />
                    </div>
                    <div>
                      <h3 className="font-medium text-[#0A0A0A] mb-1 text-sm">Endereço</h3>
                      <p className="text-sm text-[#4B5563] font-medium">
                        {settings.site_address || 'Av. Weyne Cavalcante, 720'}<br />
                        Canaã dos Carajás - PA
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white rounded-2xl border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(100,188,70,0.08)] transition-all">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="bg-[#64BC46]/10 p-3 rounded-xl flex-shrink-0">
                      <Mail className="text-[#64BC46]" size={22} />
                    </div>
                    <div>
                      <h3 className="font-medium text-[#0A0A0A] mb-1 text-sm">E-mail</h3>
                      <a
                        href={`mailto:${settings.site_email}`}
                        onClick={handleEmailClick}
                        className="text-sm text-[#4B5563] hover:text-[#EA1266] transition-colors font-medium"
                        data-testid="email-link"
                      >
                        {settings.site_email}
                      </a>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white rounded-2xl border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(234,18,102,0.08)] transition-all">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="bg-[#EA1266]/10 p-3 rounded-xl flex-shrink-0">
                      <Clock className="text-[#EA1266]" size={22} />
                    </div>
                    <div>
                      <h3 className="font-medium text-[#0A0A0A] mb-1 text-sm">Horário de Funcionamento</h3>
                      <p className="text-sm text-[#4B5563] font-medium">Segunda a Sábado, 08h às 18h</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Form */}
            <div>
              <Card className="bg-white rounded-2xl border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                <CardContent className="p-8">
                  <h2 className="text-xl font-medium text-[#0A0A0A] mb-6">Envie uma Mensagem</h2>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-xs font-medium text-[#4B5563] mb-1.5 uppercase tracking-wider">Nome Completo *</label>
                      <Input type="text" name="name" value={formData.name} onChange={handleChange} required placeholder="Seu nome" className="rounded-xl border-gray-200" data-testid="contact-name" />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-[#4B5563] mb-1.5 uppercase tracking-wider">E-mail *</label>
                        <Input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="seu@email.com" className="rounded-xl border-gray-200" data-testid="contact-email" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-[#4B5563] mb-1.5 uppercase tracking-wider">Telefone *</label>
                        <Input type="tel" name="phone" value={formData.phone} onChange={handlePhoneChange} required placeholder="(00) 00000-0000" maxLength={15} className="rounded-xl border-gray-200" data-testid="contact-phone" />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-[#4B5563] mb-1.5 uppercase tracking-wider">Assunto *</label>
                      <Input type="text" name="subject" value={formData.subject} onChange={handleChange} required placeholder="Sobre o que você quer falar?" className="rounded-xl border-gray-200" data-testid="contact-subject" />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-[#4B5563] mb-1.5 uppercase tracking-wider">Mensagem *</label>
                      <Textarea name="message" value={formData.message} onChange={handleChange} required placeholder="Digite sua mensagem..." rows={5} className="resize-none rounded-xl border-gray-200" data-testid="contact-message" />
                    </div>

                    <Button type="submit" className="w-full bg-[#EA1266] hover:bg-[#d00f58] text-white rounded-full py-6 text-sm uppercase tracking-[0.15em] font-medium shadow-lg shadow-pink-500/20 hover:shadow-pink-500/40 transition-all" data-testid="contact-submit-btn">
                      <Send className="mr-2" size={16} />
                      Enviar Mensagem
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6 md:px-12">
          <h2 className="text-2xl sm:text-3xl font-medium text-[#0A0A0A] mb-8 text-center tracking-tight">Nossa Localização</h2>
          <div className="rounded-2xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.08)]">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3977.0!2d-49.8776!3d-6.4969!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x92c5ed45f8e9d1f7%3A0x0!2sAv.%20Weyne%20Cavalcante%2C%20720%20-%20Cana%C3%A3%20dos%20Caraj%C3%A1s%20-%20PA!5e0!3m2!1spt-BR!2sbr!4v1704931200000!5m2!1spt-BR!2sbr"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Localização Disneylândia Cosméticos - Av. Weyne Cavalcante, 720"
            ></iframe>
          </div>
          <div className="mt-4 text-center">
            <a 
              href="https://www.google.com/maps/search/Av.+Weyne+Cavalcante,+720,+Canaã+dos+Carajás+-+PA"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#EA1266] hover:text-[#d00f58] font-medium text-sm"
              data-testid="open-maps-link"
            >
              <MapPin size={16} />
              Abrir no Google Maps
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
